// Per-user notification preferences — one row per category, a switch per
// channel (In-app / Push / Email). Served by notification-service through the
// gateway; every signed-in user edits only their own rows. Required channels
// are always on (checked + disabled, "Always on" hint). Each switch saves on
// its own: optimistic flip, revert + toast when the PUT fails.
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { Bell, Loader2 } from 'lucide-react';
import { getApiErrorMessage } from '@/lib/apiError';
import { Button } from '@/features/shared/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/features/shared/components/ui/card';
import { Switch } from '@/features/shared/components/ui/switch';
import {
  NOTIFICATION_CHANNELS, getNotificationPreferences, updateNotificationPreferences,
  type NotificationCategoryPrefs, type NotificationChannel,
} from '@/services/notificationPreferencesService';

const setEnabled = (
  cats: NotificationCategoryPrefs[], category: string, channel: NotificationChannel, enabled: boolean,
): NotificationCategoryPrefs[] =>
  cats.map((c) => (c.key === category
    ? { ...c, channels: { ...c.channels, [channel]: { ...c.channels[channel], enabled } } }
    : c));

export const NotificationPreferencesCard: React.FC = () => {
  const { t } = useTranslation('settings');
  const k = (key: string, dflt: string, opts: Record<string, unknown> = {}) => t(`notif_prefs.${key}`, { defaultValue: dflt, ...opts }) as string;
  const [categories, setCategories] = useState<NotificationCategoryPrefs[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [pending, setPending] = useState<Set<string>>(new Set());

  const load = async () => {
    setLoading(true);
    try { setCategories((await getNotificationPreferences()).categories); setError(null); }
    catch (e) { setError(e); }
    finally { setLoading(false); }
  };
  useEffect(() => { void load(); }, []);

  const channelLabel: Record<NotificationChannel, string> = {
    in_app: k('channel_in_app', 'In-app'),
    push: k('channel_push', 'Push'),
    email: k('channel_email', 'Email'),
    // "Desktop", not "Web push": the person is choosing where a notification
    // appears, not which protocol carries it.
    web_push: k('channel_web_push', 'Desktop'),
  };

  const toggle = async (category: string, channel: NotificationChannel, enabled: boolean) => {
    const id = `${category}:${channel}`;
    setCategories((cs) => (cs ? setEnabled(cs, category, channel, enabled) : cs));
    setPending((p) => new Set(p).add(id));
    try {
      const next = await updateNotificationPreferences([{ category, channel, enabled }]);
      if (next.categories.length) setCategories(next.categories);
    } catch (e) {
      setCategories((cs) => (cs ? setEnabled(cs, category, channel, !enabled) : cs));
      toast.error(getApiErrorMessage(e, k('save_failed', 'Could not save that preference.')));
    } finally {
      setPending((p) => { const n = new Set(p); n.delete(id); return n; });
    }
  };

  return (
    <Card data-testid="notification-preferences">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Bell className="w-5 h-5" /> {k('title', 'Notification preferences')}</CardTitle>
        <p className="text-sm text-muted-foreground">{k('subtitle', 'Choose how you want to hear about each kind of update. Some alerts are always on.')}</p>
      </CardHeader>
      <CardContent>
        {loading && !categories && (
          <p className="text-sm text-muted-foreground flex items-center gap-2" role="status">
            <Loader2 className="w-4 h-4 animate-spin" /> {k('loading', 'Loading…')}
          </p>
        )}
        {!loading && !!error && (
          <div className="text-sm text-muted-foreground flex items-center justify-between gap-3" role="alert">
            <span>{getApiErrorMessage(error, k('load_failed', 'Could not load your notification preferences.'))}</span>
            <Button variant="outline" size="sm" onClick={() => load()}>{k('retry', 'Retry')}</Button>
          </div>
        )}
        {!loading && !error && categories && categories.length === 0 && (
          <p className="text-sm text-muted-foreground" data-testid="notification-preferences-empty">{k('empty', 'No notification categories are available yet.')}</p>
        )}
        {categories && categories.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-muted-foreground">
                  <th scope="col" className="text-left font-medium pb-2 pr-3">{k('category', 'Category')}</th>
                  {NOTIFICATION_CHANNELS.map((ch) => (
                    <th key={ch} scope="col" className="font-medium pb-2 px-2 text-center whitespace-nowrap">{channelLabel[ch]}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {categories.map((c) => (
                  <tr key={c.key} data-testid={`notif-cat-${c.key}`}>
                    <td className="py-3 pr-3 align-top">
                      <div className="font-medium text-charcoal">{c.label}</div>
                      {c.description && <p className="text-xs text-muted-foreground mt-0.5">{c.description}</p>}
                    </td>
                    {NOTIFICATION_CHANNELS.map((ch) => {
                      const pref = c.channels?.[ch] ?? { enabled: false, required: false };
                      const busy = pending.has(`${c.key}:${ch}`);
                      return (
                        <td key={ch} className="py-3 px-2 text-center align-top">
                          <div className="inline-flex flex-col items-center gap-1">
                            <Switch checked={pref.required || pref.enabled} disabled={pref.required || busy}
                              aria-label={`${c.label} — ${channelLabel[ch]}`}
                              onCheckedChange={(v) => toggle(c.key, ch, v)} />
                            {pref.required && <span className="text-[10px] text-muted-foreground">{k('always_on', 'Always on')}</span>}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default NotificationPreferencesCard;
